// Location: src/components/admin/schedule/ScheduleTimingInfo.tsx
// Purpose: Display next run and last run timing information
// Scope: Shows formatted dates and relative time for schedule runs

import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Schedule } from '@/types/schedules';

interface ScheduleTimingInfoProps {
	schedule: Schedule;
}

export const ScheduleTimingInfo = React.memo(function ScheduleTimingInfo({
	schedule,
}: ScheduleTimingInfoProps) {
	return (
		<div className='space-y-1 mt-4'>
			<div className='grid grid-cols-2 gap-4'>
				<div>
					<div className='text-sm text-gray-900 dark:text-white font-bold mb-1'>
						Next run:
						{schedule.schedule?.randomRange && (
							<span className='ml-2 text-xs font-normal text-orange-500 dark:text-orange-400'>
								({schedule.schedule.randomRange.minDelay}-
								{schedule.schedule.randomRange.maxDelay} min
								random delay applied)
							</span>
						)}
					</div>
					<div className='text-sm'>
						{schedule.nextRun ? (
							<>
								{new Date(schedule.nextRun).toLocaleString(
									'en-US',
									{
										timeZone: schedule.schedule.timezone,
										hour: 'numeric',
										minute: '2-digit',
										year: 'numeric',
										month: '2-digit',
										day: '2-digit',
										hour12: true,
										timeZoneName: 'short',
									}
								)}
								{' • '}
								<span className='text-muted-foreground/70'>
									(
									{formatDistanceToNow(
										new Date(schedule.nextRun)
									)}
									)
								</span>
							</>
						) : (
							<span className='text-muted-foreground/50'>
								Never
							</span>
						)}
					</div>
				</div>
				<div className='text-left pl-8'>
					<div className='text-sm text-gray-900 dark:text-white font-bold mb-1'>
						Last run:
					</div>
					<div className='text-sm'>
						{schedule.lastRun ? (
							<>
								{new Date(schedule.lastRun).toLocaleString(
									'en-US',
									{
										timeZone: schedule.schedule.timezone,
										hour: 'numeric',
										minute: '2-digit',
										year: 'numeric',
										month: '2-digit',
										day: '2-digit',
										hour12: true,
										timeZoneName: 'short',
									}
								)}
								{' • '}
								<span className='text-muted-foreground/70'>
									(
									{formatDistanceToNow(
										new Date(schedule.lastRun)
									)}{' '}
									ago)
								</span>
							</>
						) : (
							<span className='text-muted-foreground/50'>
								Never
							</span>
						)}
					</div>
				</div>
			</div>
		</div>
	);
});
